import type { WhiteningOrderKind, WhiteningShipTo } from '@/api/types/PatientApi'
import { formatShipTo, orderSheet } from '@/features/whitening/state'
import { Button, ButtonText } from '@/ui/core/button'
import { SheetTitle } from '@/ui/core/sheet'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { router } from 'expo-router'
import { View } from 'react-native'

/**
 * Where it ships, and the button that actually orders it.
 *
 * Ordering is a confirmation rather than a page-level form. The cards carry one button each and
 * nothing else about the transaction — no address panel, no caveat, no small print — because
 * before somebody has decided to claim a kit, where it would ship is not a question they are
 * asking. Once they have pressed, it is the only question, and it gets a sheet to itself with
 * the answer and one way to change it.
 *
 * The mutation fires from here, which is also where the server's refusals land: a missing
 * address is the one a member can fix, and the sheet is already the place that shows it.
 */
export const ShipToSheet = ({
	kind,
	shipTo,
	scanOnFile,
	isBusy,
	onConfirm,
	onClose,
}: {
	kind: WhiteningOrderKind
	/** Absent when we hold no address at all, which turns the sheet into the request for one. */
	shipTo?: WhiteningShipTo
	scanOnFile: boolean
	isBusy: boolean
	onConfirm: () => void
	onClose: () => void
}) => {
	const copy = orderSheet(kind, scanOnFile)

	const toAddressForm = () => {
		onClose()
		router.navigate({ pathname: '/profile', params: { sheet: 'address' } })
	}

	return (
		<View className="pt-2">
			<SheetTitle glyph="pin">Ship To</SheetTitle>
			<VStack gap={24} className="px-6 pb-8">
				{shipTo ? (
					<>
						{/* The quiet inner panel a white surface uses, at the brand's own radius. */}
						<HStack className="items-center rounded-brand bg-surface px-4 py-3" gap={12}>
							<VStack gap={2} className="flex-1">
								<Text size="body0" weight="bold">
									{shipTo.name}
								</Text>
								<Text size="body1" tone="soft">
									{formatShipTo(shipTo)}
								</Text>
							</VStack>
							{/* The shared link, so this reads as every other secondary action in the
							    app. It leaves for the profile's address form rather than carrying one:
							    an address is written in one place, and two forms for it is how two
							    screens come to disagree about where somebody lives. */}
							<Button variant="link" fullWidth={false} onPress={toAddressForm}>
								<ButtonText>Change</ButtonText>
							</Button>
						</HStack>

						<VStack gap={8}>
							<Button
								variant="primary"
								size="lg"
								fullWidth
								isLoading={isBusy}
								onPress={onConfirm}
							>
								<ButtonText>{copy.button}</ButtonText>
							</Button>
							{/* The caveat, under the button it is about. It is a statement of what
							    happens after a press, so this is the first place it has been true. */}
							{!!copy.caption && (
								<Text size="body2" tone="soft" className="text-center">
									{copy.caption}
								</Text>
							)}
						</VStack>
					</>
				) : (
					// No address on file. The button is not drawn disabled with an explanation
					// beside it: the thing standing between this member and a kit is one form, so
					// the sheet asks for that instead of showing them a control that cannot work.
					<VStack gap={16}>
						<Text size="body0">
							We don&rsquo;t have an address for you yet. Add one and we&rsquo;ll send
							your kit straight out.
						</Text>
						<Button variant="primary" size="lg" fullWidth onPress={toAddressForm}>
							<ButtonText>Add Your Address</ButtonText>
						</Button>
					</VStack>
				)}
			</VStack>
		</View>
	)
}
